import PropTypes from "prop-types"
import React, { useMemo, useState, useCallback, useRef, useEffect } from "react"
import { useQuery } from "@apollo/client"
import { GET_REFERENTIAL_HIERARCHY } from "./hierarchyQuery"
import { useClickOutside } from "Hooks/useClickOutside"
import { getContrastingTextColor } from "Shared/utils/getContrastedColor"
import {
  avecAncetres,
  buildHierarchyTree,
  collectSubtreeIds,
  combinerAutorises,
  idsSelonValeurs,
  pruneHierarchyNodes,
  selectionSuivanteCycle,
} from "./treeFilter.utils"
import {
  PickerBox,
  Row,
  Caret,
  Empty,
  Container,
  TriggerPill,
  TriggerLabel,
  ClearBtn,
  TriggerCaret,
  DropdownPanel,
} from "./index.style"

const HierarchicalFilter = ({
  referentialId,
  relationId,
  parentSide = "left",
  labelColumnId,
  allowedIds = null,
  displayMode = "dropdown",
  value,
  onSelectFilterValue,
  onRemoveSelectedValue,
  placeholder,
  widgetTraduction,
  // Props de style partagées avec les autres filtres (dropdownList/rangeFilter)
  activeBorder,
  dropdownBorderRadius,
  dropdownBorderColor,
  dropdownBorderSize,
  dropdownBorderStyle,
  defaultColor,
  /**
   * Entités à EXCLURE de l'arbre, ou seules entités à PROPOSER : `{ mode, values }` sur les libellés —
   * même réglage que les listes déroulantes. Les descendants gardés d'une entité écartée se
   * raccrochent à leur premier ancêtre gardé (`idsSelonValeurs`, `pruneHierarchyNodes`).
   */
  valueFilter,
  /**
   * Garde les ANCÊTRES des entités autorisées par les conditions (`avecAncetres`) : l'arbre restreint
   * aux entités présentes dans le dashboard garde sa forme. Opt-in.
   */
  keepAncestors = false,
  /**
   * Clic en trois temps (`selectionSuivanteCycle`) : le nœud seul, puis son sous-arbre, puis rien.
   * Opt-in — par défaut, un clic coche le nœud ET son sous-arbre, comme avant.
   */
  selectionCycle = false,
}) => {
  // Fond configurable comme le Sélecteur : la pastille prend `defaultColor` en fond.
  // Un nouveau widget est initialisé à la couleur primaire du tenant (getWidgetDefaultProps),
  // texte auto-contrasté ; repli blanc + texte par défaut seulement si aucun defaultColor.
  // Les cases reprennent l'accent tenant.
  const pillBg = defaultColor || undefined
  const pillTextColor = defaultColor ? getContrastingTextColor(defaultColor) : undefined
  const accentColor = defaultColor || undefined
  const { data, loading } = useQuery(GET_REFERENTIAL_HIERARCHY, {
    variables: { referentialId, relationId, parentSide, labelColumnId },
    skip: !referentialId || !relationId,
    fetchPolicy: "cache-first",
  })

  // `allowedIds` : fiches qui respectent les conditions du widget (null = aucune condition).
  // + `valueFilter` : les deux contraintes se cumulent (intersection).
  const cleReglage = JSON.stringify(valueFilter ?? null)
  const nodes = useMemo(() => {
    const brutes = data?.getReferentialHierarchy ?? []
    const autorises = keepAncestors ? avecAncetres(brutes, allowedIds) : allowedIds
    return pruneHierarchyNodes(brutes, combinerAutorises(autorises, idsSelonValeurs(brutes, valueFilter)))
    // `valueFilter` par sa forme sérialisée : l'objet est recréé à chaque rendu du parent.
  }, [data, allowedIds, cleReglage, keepAncestors])
  const { roots, byId } = useMemo(() => buildHierarchyTree(nodes), [nodes])
  const [collapsed, setCollapsed] = useState(() => new Set())
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef()
  const checked = useMemo(() => new Set(Array.isArray(value) ? value : []), [value])

  useClickOutside(containerRef, () => {
    if (isOpen) setIsOpen(false)
  })

  // Comme les autres filtres (dropdownList/rangeFilter/calendar) : à l'ouverture on lève
  // l'overflow du bloc de dashboard (#resizable) pour que le dropdown ne soit pas clippé.
  useEffect(() => {
    const fixedLayout = document.getElementById("resizable")
    const screenshotContainer = document.getElementById("screenshotContainer")
    if (isOpen) {
      if (fixedLayout) {
        fixedLayout.style.removeProperty("overflow")
        fixedLayout.style.removeProperty("overflow-y")
      }
      if (screenshotContainer) screenshotContainer.style.setProperty("overflow-y", "auto")
    } else {
      if (fixedLayout) fixedLayout.style.setProperty("overflow-y", "auto")
      if (screenshotContainer) screenshotContainer.style.removeProperty("overflow-y")
    }
    return () => {
      const fl = document.getElementById("resizable")
      if (fl) fl.style.setProperty("overflow-y", "auto")
    }
  }, [isOpen])

  // Map enfant → parent (nœuds bruts) pour résumer la sélection par ses "racines".
  const parentOf = useMemo(() => {
    const m = {}
    for (const n of nodes) m[n.id] = n.parentId
    return m
  }, [nodes])

  // Anti-cycle : on s'appuie sur collectSubtreeIds (déjà gardé par un Set) plutôt que sur
  // une récursion node.children.every() qui bouclerait sur des données cycliques/multi-parents.
  const isFullyChecked = useCallback(
    node => collectSubtreeIds(byId, node.id).every(id => checked.has(id)),
    [byId, checked]
  )
  const isPartiallyChecked = useCallback(
    node => {
      const subtree = collectSubtreeIds(byId, node.id)
      const hit = subtree.filter(id => checked.has(id)).length
      return hit > 0 && hit < subtree.length
    },
    [byId, checked]
  )

  const toggle = node => {
    let next
    if (selectionCycle) {
      next = selectionSuivanteCycle(byId, checked, node.id)
    } else {
      const subtree = collectSubtreeIds(byId, node.id)
      next = new Set(checked)
      const turnOn = !subtree.every(id => next.has(id))
      subtree.forEach(id => (turnOn ? next.add(id) : next.delete(id)))
    }
    const ids = Array.from(next)
    if (!ids.length) onRemoveSelectedValue()
    else onSelectFilterValue(ids.map(id => ({ value: id })))
  }

  const renderNode = (node, depth, seen = new Set()) => {
    // Garde anti-cycle : ne jamais re-rendre un nœud déjà traversé dans cette branche.
    if (seen.has(node.id)) return null
    const nextSeen = new Set(seen)
    nextSeen.add(node.id)
    const hasChildren = node.children.length > 0
    const isCollapsed = collapsed.has(node.id)
    return (
      <React.Fragment key={node.id}>
        <Row depth={depth}>
          {hasChildren ? (
            <Caret
              type="button"
              onClick={() =>
                setCollapsed(prev => {
                  const n = new Set(prev)
                  n.has(node.id) ? n.delete(node.id) : n.add(node.id)
                  return n
                })
              }
            >
              <i className={`fa-solid fa-chevron-${isCollapsed ? "right" : "down"}`} />
            </Caret>
          ) : (
            <span style={{ width: 14 }} />
          )}
          <input
            type="checkbox"
            // En mode cycle, la case dit si le nœud LUI-MÊME est retenu : « DG seule » doit se lire
            // cochée, pas « partielle ». Le tiret ne signale alors qu'un descendant coché sans lui.
            checked={selectionCycle ? checked.has(node.id) : isFullyChecked(node)}
            ref={el => {
              if (!el) return
              el.indeterminate = selectionCycle
                ? !checked.has(node.id) && isPartiallyChecked(node)
                : !isFullyChecked(node) && isPartiallyChecked(node)
            }}
            onChange={() => toggle(node)}
          />
          <span>{node.label}</span>
        </Row>
        {hasChildren && !isCollapsed && node.children.map(c => renderNode(c, depth + 1, nextSeen))}
      </React.Fragment>
    )
  }

  if (!referentialId || !relationId)
    return <Empty>{widgetTraduction?.CONFIGURE ?? "Configurer la relation"}</Empty>

  // Corps de l'arbre (partagé par les deux modes)
  const treeBody = loading ? (
    <Empty>{widgetTraduction?.LOADING ?? "Chargement…"}</Empty>
  ) : !roots.length ? (
    <Empty>{widgetTraduction?.EMPTY ?? "Aucun élément"}</Empty>
  ) : (
    <PickerBox $accent={accentColor}>{roots.map(r => renderNode(r, 0))}</PickerBox>
  )

  // Mode "arbre déplié" : l'arbre occupe tout le widget.
  if (displayMode === "inline") return treeBody

  // Mode "sélecteur" (défaut) : pill compacte + dropdown, comme les filtres normaux.
  const labelOf = id => byId.get(id)?.label ?? id
  // Racines de la sélection = nœuds cochés dont le parent n'est pas coché (résumé lisible).
  const selectionRoots = Array.from(checked).filter(id => !checked.has(parentOf[id]))
  const summary =
    selectionRoots.length === 0
      ? placeholder || widgetTraduction?.PLACEHOLDER || "Filtrer par hiérarchie"
      : selectionRoots.length <= 2
        ? selectionRoots.map(labelOf).join(", ")
        : `${selectionRoots.length} ${widgetTraduction?.SELECTED ?? "sélectionnés"}`

  const clearAll = e => {
    e.stopPropagation()
    onRemoveSelectedValue()
  }

  return (
    <Container ref={containerRef}>
      <TriggerPill
        onClick={() => setIsOpen(o => !o)}
        $activeBorder={activeBorder}
        $borderColor={dropdownBorderColor}
        $borderSize={dropdownBorderSize}
        $borderStyle={dropdownBorderStyle}
        $borderRadius={dropdownBorderRadius}
        $bg={pillBg}
        $color={pillTextColor}
      >
        <TriggerLabel $placeholder={selectionRoots.length === 0}>{summary}</TriggerLabel>
        {selectionRoots.length > 0 && (
          <ClearBtn type="button" onClick={clearAll} title={widgetTraduction?.CLEAR ?? "Effacer"}>
            <i className="fas fa-times" />
          </ClearBtn>
        )}
        <TriggerCaret className={`fas fa-chevron-${isOpen ? "up" : "down"}`} />
      </TriggerPill>
      {isOpen && <DropdownPanel>{treeBody}</DropdownPanel>}
    </Container>
  )
}

HierarchicalFilter.propTypes = {
  /** Fiches qui respectent les conditions du widget ; `null` = aucune condition (arbre complet). */
  allowedIds: PropTypes.arrayOf(PropTypes.string),
  /** Exclure ou ne proposer que certaines entités — cf. le bloc du composant. */
  valueFilter: PropTypes.shape({
    mode: PropTypes.oneOf(["exclude", "include"]),
    values: PropTypes.array,
  }),
  keepAncestors: PropTypes.bool,
  selectionCycle: PropTypes.bool,
}

export default HierarchicalFilter
